import { useState } from 'react';
import { COPY, programLabel } from '../content';
import { apiGet, apiPost, type MemberListItem } from '../api';
import { useAsync, navigate } from '../hooks';
import { lastSeen, pluralize } from '../format';
import { Spinner, LoadError, Empty, Pill, lifecyclePill } from '../components/ui';

const PROGRAMS = ['brand_ambassador', 'demo_center', 'pro_content_creator', 'inflatable_boarder'];

export function Members({ onChange }: { onChange: () => void }) {
  const [q, setQ] = useState('');
  const [program, setProgram] = useState('');
  const [status, setStatus] = useState('');
  const [active, setActive] = useState(false);
  const [adding, setAdding] = useState(false);

  const query = new URLSearchParams();
  if (q.trim()) query.set('q', q.trim());
  if (program) query.set('program', program);
  if (status) query.set('status', status);
  if (active) query.set('active', '30d');

  const { data, loading, error, reload } = useAsync(
    () => apiGet<{ items: MemberListItem[] }>(`/v1/admin/members?${query.toString()}`),
    [q, program, status, active],
  );
  const items = data?.items ?? [];

  return (
    <div>
      <div className="row between">
        <h1 className="screen-title">{COPY.members.title}</h1>
        <button className="secondary" onClick={() => setAdding((v) => !v)}>
          {COPY.members.addByHand}
        </button>
      </div>
      <p className="screen-sub">{COPY.members.sub}</p>

      {adding ? <AddMember onDone={() => { setAdding(false); reload(); onChange(); }} /> : null}

      <div className="toolbar">
        <input
          className="grow"
          placeholder={COPY.members.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={program} onChange={(e) => setProgram(e.target.value)} aria-label="Program filter">
          <option value="">All programs</option>
          {PROGRAMS.map((p) => (
            <option key={p} value={p}>
              {programLabel(p)}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Standing filter">
          <option value="">Any standing</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="closed">Closed</option>
        </select>
        <button
          className={`fchip ${active ? 'on' : ''}`}
          aria-pressed={active}
          onClick={() => setActive((v) => !v)}
        >
          {active ? '✓ ' : ''}
          {COPY.members.activeFilter}
        </button>
      </div>

      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}
      {data && items.length === 0 ? <Empty>{COPY.members.empty}</Empty> : null}

      {items.length > 0 ? (
        <div className="card" style={{ padding: 0 }}>
          {items.map((m) => (
            <button
              key={m.id}
              className="ghost list-row"
              style={{ width: '100%', textAlign: 'left', padding: '12px 16px', borderRadius: 0 }}
              onClick={() => navigate(`members/${m.id}`)}
            >
              <div className="grow">
                <div className="row" style={{ gap: 8 }}>
                  <strong>{m.alias || m.realName || m.name}</strong>
                  {m.crewNumber ? <span className="muted small">#{m.crewNumber}</span> : null}
                  {lifecyclePill(m.lifecycleStatus)}
                  {m.accountStatus === 'suspended' ? <Pill tone="red">Banned</Pill> : null}
                </div>
                {/* Real name + email, admin only (never shown to members). */}
                <div className="small muted">
                  {m.alias && m.realName ? `${m.realName}` : ''}
                  {m.email ? `${m.alias && m.realName ? ' | ' : ''}${m.email}` : ''}
                </div>
                <div className="small muted">
                  {programLabel(m.program)}
                  {' | '}
                  {m.sharingToolsOn ? <span className="mono">{m.code ?? 'code pending'}</span> : COPY.members.noSharing}
                  {' | '}
                  {pluralize(m.referralCount, 'referral')}
                </div>
              </div>
              <div className="small muted" style={{ textAlign: 'right' }}>
                {lastSeen(m.lastActivityIso)}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AddMember({ onDone }: { onDone: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [program, setProgram] = useState('brand_ambassador');
  const [crewNumber, setCrewNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = { program };
      if (firstName.trim()) body.firstName = firstName.trim();
      if (crewNumber.trim()) body.crewNumber = Number(crewNumber.trim());
      await apiPost('/v1/admin/members', body);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <h3>Add a member by hand</h3>
      <input placeholder="First name (optional)" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      <select value={program} onChange={(e) => setProgram(e.target.value)} aria-label="Program">
        {PROGRAMS.map((p) => (
          <option key={p} value={p}>
            {programLabel(p)}
          </option>
        ))}
      </select>
      <input
        placeholder="Crew number (optional, auto if blank)"
        inputMode="numeric"
        value={crewNumber}
        onChange={(e) => setCrewNumber(e.target.value)}
      />
      {err ? <div className="banner err">{err}</div> : null}
      <div className="row">
        <button disabled={busy} onClick={save}>
          {COPY.common.save}
        </button>
      </div>
    </div>
  );
}
